import React, { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import AssetRequestList from "./AssetRequestList";
import AccessRequestList from "./AccessRequestList";
import PageShell from "../../v2/components/shared/page-shell/page-shell.tsx";
import { Card } from "../../v2/components/shared/card/card.tsx";
import { useBreadcrumbs } from "../../v2/context/breadcrumb/breadcrumb-context.tsx";
import {
  ButtonLink,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../v2/components/ui/index.ts";

import styles from "./it-requests-tabs.module.css";

const TAB_VALUES = ["asset", "access"];

const ITAssetAndAccessRequestsTabs = ({ title, description }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const { setBreadcrumbs } = useBreadcrumbs();
  const handleTotalRequestChange = () => {
    // Totals are now rendered inside the table footer via `totalLabel`.
    // Kept as a no-op so child components retain the same `onTotalRequestChange` callback signature.
  };
  const tabs = [
    { label: "Asset Requests", component: AssetRequestList },
    { label: "Access Requests", component: AccessRequestList },
  ];

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "access") {
      setActiveTabIndex(1); // Switch to Access Requests tab
    } else if (tabParam === "asset") {
      setActiveTabIndex(0); // Switch to Asset Requests tab
    }
  }, [searchParams]);

  const handleTabChange = (index) => {
    setActiveTabIndex(index);
    const tabName = index === 0 ? "asset" : "access";
    setSearchParams({ tab: tabName });
  };

  const activeTabValue = TAB_VALUES[activeTabIndex] ?? TAB_VALUES[0];
  const activeTabLabel = tabs[activeTabIndex]?.label ?? tabs[0].label;

  useEffect(() => {
    setBreadcrumbs([
      { label: "IT Requests" },
      { label: activeTabLabel },
    ]);
    return () => setBreadcrumbs([]);
  }, [activeTabLabel, setBreadcrumbs, title]);

  return (
    <PageShell>
      <Card className={styles.tableCard} padding="5xl">
        <Tabs
          value={activeTabValue}
          onValueChange={(value) => {
            const nextIndex = TAB_VALUES.indexOf(value);
            handleTabChange(nextIndex === -1 ? 0 : nextIndex);
          }}
        >
          <div className={styles.tabBar}>
            <TabsList className={styles.tabsList}>
              {tabs.map((item, index) => (
                <TabsTrigger
                  key={item.label}
                  value={TAB_VALUES[index]}
                  className={styles.tabTrigger}
                >
                  {item.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <div className={styles.actionsSlot}>
              <ButtonLink
                to={`/dashboard/createAssetRequest?tab=${activeTabValue}`}
                variant="accent"
                startIcon={<Plus size={16} strokeWidth={2} aria-hidden />}
              >
                Create Request
              </ButtonLink>
            </div>
          </div>

          {tabs.map((item, index) => {
            const Component = item.component;
            const value = TAB_VALUES[index];
            return (
              <TabsContent
                key={item.label}
                value={value}
                className={styles.tabPanel}
              >
                {activeTabIndex === index && (
                  <Component
                    title={item.label}
                    description=""
                    onTotalRequestChange={handleTotalRequestChange}
                  />
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </Card>
    </PageShell>
  );
};

export default ITAssetAndAccessRequestsTabs;
